import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useChangePassword } from '../hooks/useChangePassword';
import {
  changePasswordSchema,
  type ChangePasswordFormValues,
} from '../schemas/change-password-schema';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ChangePasswordModal({ open, onClose, onSuccess }: ChangePasswordModalProps) {
  const { changePassword, isSubmitting, reset } = useChangePassword();
  const [pendingValues, setPendingValues] = useState<ChangePasswordFormValues | null>(null);

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const [currentPasswordValue, newPasswordValue, confirmPasswordValue] = form.watch([
    'currentPassword',
    'newPassword',
    'confirmPassword',
  ]);
  const hasAnyValue = Boolean(currentPasswordValue || newPasswordValue || confirmPasswordValue);

  const onSubmit = (values: ChangePasswordFormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = async () => {
    if (!pendingValues) return;
    try {
      await changePassword(pendingValues);
      setPendingValues(null);
      form.reset();
      reset();
      onSuccess?.();
      onClose();
    } catch {
      setPendingValues(null);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    form.reset();
    reset();
    onClose();
  };

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<KeyRound />}
        onOpenChange={handleClose}
        title="Cambiar contraseña"
        description="Ingresa tu contraseña actual y define una nueva. Cerraremos las demás sesiones activas y recibirás un correo de confirmación."
        className="sm:max-w-[40rem]"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                data-testid="change-password-cancel-button"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isSubmitting || !hasAnyValue}
                data-testid="change-password-submit-button"
              >
                Guardar cambios
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <div className="grid grid-cols-1 gap-[1.23rem]">
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Contraseña actual"
                    placeholder="Ingresa tu contraseña actual"
                    type="password"
                    autoComplete="current-password"
                    {...field}
                    disabled={isSubmitting}
                    error={form.formState.errors.currentPassword?.message}
                    data-testid="change-password-current-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.23rem]">
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Nueva contraseña"
                    placeholder="Mínimo 8 caracteres"
                    type="password"
                    autoComplete="new-password"
                    {...field}
                    disabled={isSubmitting}
                    error={form.formState.errors.newPassword?.message}
                    data-testid="change-password-new-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Confirmar nueva contraseña"
                    placeholder="Repite la contraseña"
                    type="password"
                    autoComplete="new-password"
                    {...field}
                    disabled={isSubmitting}
                    error={form.formState.errors.confirmPassword?.message}
                    data-testid="change-password-confirm-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <p className="text-xs text-primary">
          Debe contener mínimo 8 caracteres, una mayúscula, una minúscula, un número y un carácter
          especial.
        </p>
      </ModalForm>

      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isSubmitting && !v && setPendingValues(null)}
        title="Confirmar cambio de contraseña"
        description="Tu contraseña será actualizada, las demás sesiones activas se cerrarán y recibirás un correo de confirmación. ¿Continuar?"
        onConfirm={handleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isSubmitting}
        onCancel={() => setPendingValues(null)}
        data-testid="change-password-confirm-dialog"
      />
    </>
  );
}
